import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Table, Spinner } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';
import Header from './common/header';
import ConfigModal from './configModal';
import AutoUpdateModal from './AutoUpdateModal';
import ManualUpdateModal from './ManualUpdateModal';
import RestoreBackupModal from './RestoreBackupModal';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const StoragePage = () => {
    const { user } = useAuth();

    // State management
    const [updateHistory, setUpdateHistory] = useState([]);
    const [currentUpdate, setCurrentUpdate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modernAlert, setModernAlert] = useState({ show: false, type: '', message: '' });
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [showAutoUpdateModal, setShowAutoUpdateModal] = useState(false);
    const [showManualUpdateModal, setShowManualUpdateModal] = useState(false);
    const [showRestoreBackupModal, setShowRestoreBackupModal] = useState(false);
    const [currentConfig, setCurrentConfig] = useState({});
    const [backupToRestore, setBackupToRestore] = useState({ caminho: '', nome: '' });

    const isAdmin = user?.acesso === 'Administrador';

    /**
     * Show modern alert notification
     *
     * @param {string} message - Message to display
     * @param {string} type - Alert type ('success' or 'error')
     */
    const showModernAlert = (message, type = 'success') => {
        setModernAlert({ show: true, type, message });
        setTimeout(() => {
            setModernAlert({ show: false, type: '', message: '' });
        }, 5000);
    };

    /**
     * Load initial data on component mount
     */
    useEffect(() => {
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Load update history, current update and storage status
     */
    const loadInitialData = async () => {
        try {
            setLoading(true);

            // Load update history usando API /listar-historico
            if (true) {
                const historyResponse = await apiService.listarHistoricoRepositorio();
                if (historyResponse.success && historyResponse.historico) {
                    // Novo formato: { data: '2025-12-02_14-30-00', caminho: '/path', log: 'texto' }
                    const mappedHistory = historyResponse.historico.map(item => ({
                        data: item.data,
                        identificador: item.log,
                        caminho: item.caminho
                    }));
                    setUpdateHistory(mappedHistory);
                }

                // Load current update usando API /caminho-atual
                const currentResponse = await apiService.obterCaminhoAtualRepositorio();
                if (currentResponse.success && currentResponse.atual) {
                    setCurrentUpdate({
                        data: currentResponse.atual.data,
                        identificador: currentResponse.atual.log,
                        caminho: currentResponse.atual.caminho
                    });
                }
            }

        } catch (error) {
            console.error('Error loading initial data:', error);
            showModernAlert(`Erro ao carregar dados: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Format Unix timestamp to DD/MM/YYYY HH:MM:SS
     */
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp * 1000);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    /**
     * Handle manual update - Receives files and log message from ManualUpdateModal and executes update
     */
    const handleManualUpdate = async (files, message) => {
        if (!isAdmin) {
            showModernAlert('Apenas administradores podem executar atualizações', 'error');
            return;
        }

        // Store files temporarily for executeManualUpdate
        window.pendingManualUpdateFiles = files;
        await executeManualUpdate(message);
    };

    /**
     * Execute manual update
     * Envia 3 arquivos CSV para o backend via API /atualizar-manual
     */
    const executeManualUpdate = async (message) => {
        const files = window.pendingManualUpdateFiles;
        if (!files) return;

        try {
            setLoading(true);

            // Criar FormData para upload dos arquivos
            const formData = new FormData();
            formData.append('entregas', files.entregas);
            formData.append('trl_crl_datas', files.trl_crl_datas);
            formData.append('orcamento_resumo', files.orcamento_resumo);
            formData.append('mensagemLog', message); // Adicionar mensagem de log

            // Usar API POST /atualizar-manual
            const response = await apiService.atualizarRepositorioManual(formData);

            if (response.success) {
                showModernAlert(
                    response.message || 'Atualização manual concluída com sucesso!',
                    'success'
                );
                await loadInitialData();
            } else {
                showModernAlert(`Falha na atualização: ${response.error || 'Erro desconhecido'}`, 'error');
            }

        } catch (error) {
            console.error('Manual update error:', error);
            showModernAlert(`Erro na atualização manual: ${error.message}`, 'error');
        } finally {
            setLoading(false);
            window.pendingManualUpdateFiles = null;
        }
    };

    /**
     * Handle automatic update - Receives log message from AutoUpdateModal and executes update
     */
    const handleAutoUpdate = async (message) => {
        if (!isAdmin) {
            showModernAlert('Apenas administradores podem executar atualizações', 'error');
            return;
        }

        await executeAutoUpdate(message);
    };

    /**
     * Execute automatic update
     * Uses API POST /buscar-atualizacao (busca dados externos e atualiza)
     */
    const executeAutoUpdate = async (message) => {
        try {
            setLoading(true);

            // Usar API POST /buscar-atualizacao
            const response = await apiService.buscarAtualizacaoRepositorio("at: " + message);

            if (response.success) {
                showModernAlert(
                    response.message || 'Atualização automática concluída com sucesso!',
                    'success'
                );
                await loadInitialData();
            } else {
                showModernAlert(`Falha na atualização automática: ${response.error || 'Erro desconhecido'}`, 'error');
            }

        } catch (error) {
            console.error('Automatic update error:', error);
            showModernAlert(`Erro na atualização automática: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle configuration settings
     * Carrega caminhos configurados do repositório
     */
    const handleConfigSettings = async () => {
        if (!isAdmin) {
            showModernAlert('Apenas administradores podem acessar configurações', 'error');
            return;
        }

        try {
            // Load current configuration usando API GET /caminhos
            const configResponse = await apiService.getRepositorioCaminhos();
            if (configResponse.success && configResponse.caminhos) {
                setCurrentConfig(configResponse.caminhos);
            }
        } catch (error) {
            console.error('Error loading config:', error);
            showModernAlert(`Erro ao carregar configurações: ${error.message}`, 'error');
            setCurrentConfig({});
        }

        setShowConfigModal(true);
    };

    /**
     * Handle configuration save from modal
     * Salva agendamento de atualizações automáticas
     */
    const handleConfigSave = async (configData) => {
        try {
            // Salvar configuração de agendamento
            // Por enquanto apenas salva localmente ou no backend se houver API
            showModernAlert(`Configuração salva! Próxima atualização em ${configData.nextUpdateDate}`, 'success');
            setShowConfigModal(false);

            // Futuramente pode implementar API PUT /config para salvar agendamento no backend
            // await apiService.salvarConfigRepositorio(configData);
        } catch (error) {
            showModernAlert(`Erro ao salvar configuração: ${error.message}`, 'error');
        }
    };

    /**
     * Handle restore backup - Opens RestoreBackupModal for confirmation
     */
    const handleRestoreBackup = (caminho, nome) => {
        if (!isAdmin) {
            showModernAlert('Apenas administradores podem restaurar backups', 'error');
            return;
        }

        setBackupToRestore({ caminho, nome });
        setShowRestoreBackupModal(true);
    };

    /**
     * Confirm restore backup - Called when user confirms in modal
     */
    const confirmRestoreBackup = async () => {
        try {
            setLoading(true);

            // Usar API POST /restaurar
            const response = await apiService.restaurarRepositorio(backupToRestore.caminho);

            if (response.success) {
                showModernAlert(
                    response.message || 'Backup restaurado com sucesso!',
                    'success'
                );
                await loadInitialData();
            } else {
                showModernAlert(`Falha ao restaurar backup: ${response.error || 'Erro desconhecido'}`, 'error');
            }

        } catch (error) {
            console.error('Restore backup error:', error);
            showModernAlert(`Erro ao restaurar backup: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Format date for display in history table
     * Converts from '2025-12-02_14-30-00' to '02/12/2025 14:30:00'
     */
    const formatDate = (dateString) => {
        try {
            // Format: YYYY-MM-DD_HH-MM-SS
            const [datePart, timePart] = dateString.split('_');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes, seconds] = timePart.split('-');
            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        } catch (error) {
            return dateString;
        }
    };

    return (
        <div>
            {/* Header */}
            <Header />

            <Container className="px-5">
                {/* Back Button - Centered */}
                <Row className="mb-4">
                    <Col className="text-center">
                        <Button
                            variant="primary"
                            onClick={() => window.history.back()}
                            style={{
                                backgroundColor: '#003B6B',
                                borderColor: '#003B6B',
                                borderRadius: '8px',
                                padding: '10px 30px',
                                fontWeight: 'bold'
                            }}
                        >
                            Voltar
                        </Button>
                    </Col>
                </Row>

                {/* Modern Alert Notification */}
                {modernAlert.show && (
                    <div
                        style={{
                            position: 'fixed',
                            top: '2rem',
                            right: '2rem',
                            zIndex: 9999,
                            maxWidth: '400px',
                            background: modernAlert.type === 'success' ?
                                '#003B6B' :
                                '#dc3545',
                            color: 'white',
                            padding: '1rem 1.5rem',
                            borderRadius: '1rem',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            border: 'none',
                            fontWeight: '600',
                            fontSize: '0.95rem',
                            animation: 'slideInRight 0.5s ease-out',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                        }}
                    >
                        <div style={{
                            width: '2rem',
                            height: '2rem',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            {modernAlert.type === 'success' ? '✓' : '✕'}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '700', marginBottom: '0.25rem' }}>
                                {modernAlert.type === 'success' ? 'Sucesso!' : 'Erro!'}
                            </div>
                            <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>
                                {modernAlert.message}
                            </div>
                        </div>
                        <button
                            onClick={() => setModernAlert({ show: false, type: '', message: '' })}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                padding: '0.25rem',
                                borderRadius: '0.25rem',
                                opacity: 0.7,
                                fontSize: '1.2rem',
                                lineHeight: 1,
                                transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = 1}
                            onMouseLeave={(e) => e.target.style.opacity = 0.7}
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Database Update Section */}
                <Row className="mb-4">
                    <Col>
                        <div style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            {/* Header */}
                            <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
                                <span className="text-lg font-semibold">Atualizar banco de dados</span>
                            </div>

                            {/* Content */}
                            <div className='text-center' style={{ padding: '30px' }}>
                                <div className="d-flex flex-column align-items-center">
                                    {/* Automatic Update Button with Settings - Top Row */}
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <Button
                                                variant="primary"
                                                size="lg"
                                                onClick={() => setShowAutoUpdateModal(true)}
                                                disabled={loading || !isAdmin}
                                                style={{
                                                    backgroundColor: '#003B6B',
                                                    borderColor: '#003B6B',
                                                    padding: '12px 25px',
                                                    fontWeight: 'bold',
                                                    marginRight: '10px',
                                                    minWidth: '200px',
                                                    opacity: !isAdmin ? 0.6 : 1
                                                }}
                                                title={!isAdmin ? 'Apenas administradores' : ''}
                                            >
                                                {loading ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Atualizando...
                                                    </>
                                                ) : (
                                                    'Atualização automática'
                                                )}
                                            </Button>
                                            <Button
                                                variant="primary"
                                                size="lg"
                                                onClick={handleConfigSettings}
                                                disabled={loading || !isAdmin}
                                                style={{
                                                    backgroundColor: '#003B6B',
                                                    borderColor: '#003B6B',
                                                    padding: '12px 15px',
                                                    opacity: !isAdmin ? 0.6 : 1
                                                }}
                                                title={!isAdmin ? 'Apenas administradores' : ''}
                                            >
                                                <FaCog />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Manual Update Button - Bottom Row */}
                                    <div className="mb-3">
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            onClick={() => setShowManualUpdateModal(true)}
                                            disabled={loading || !isAdmin}
                                            style={{
                                                backgroundColor: '#003B6B',
                                                borderColor: '#003B6B',
                                                padding: '12px 25px',
                                                fontWeight: 'bold',
                                                width: '335px', // Same width as auto update + config button + margin
                                                opacity: !isAdmin ? 0.6 : 1
                                            }}
                                            title={!isAdmin ? 'Apenas administradores' : ''}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Atualizando...
                                                </>
                                            ) : (
                                                'Atualização manual'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Current Update Section */}
                <Row className="mb-4">
                    <Col>
                        <div style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            {/* Header */}
                            <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
                                <span className="text-lg font-semibold">Última atualização realizada</span>
                            </div>

                            {/* Content */}
                            <div style={{ padding: '20px' }}>
                                {loading && !currentUpdate ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </Spinner>
                                    </div>
                                ) : !currentUpdate ? (
                                    <div className="text-center py-4 text-muted">
                                        Nenhuma atualização disponível
                                    </div>
                                ) : (
                                    <Table bordered className="no-hover-table" style={{ margin: 0 }}>
                                        <thead style={{ backgroundColor: '#e9ecef' }}>
                                            <tr>
                                                <th style={{
                                                    textAlign: 'center',
                                                    fontWeight: 'bold',
                                                    padding: '12px',
                                                    borderRight: '1px solid #dee2e6'
                                                }}>
                                                    Data
                                                </th>
                                                <th style={{
                                                    textAlign: 'center',
                                                    fontWeight: 'bold',
                                                    padding: '12px'
                                                }}>
                                                    Identificador
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={{
                                                    textAlign: 'center',
                                                    padding: '12px',
                                                    borderRight: '1px solid #dee2e6'
                                                }}>
                                                    {formatDate(currentUpdate.data)}
                                                </td>
                                                <td style={{
                                                    textAlign: 'center',
                                                    padding: '12px'
                                                }}>
                                                    {currentUpdate.identificador}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Update History Section */}
                <Row>
                    <Col>
                        <div style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            {/* Header */}
                            <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
                                <span className="text-lg font-semibold">Atualizações anteriores</span>
                            </div>

                            {/* Content */}
                            <div style={{ padding: '20px' }}>
                                {loading && updateHistory.length === 0 ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </Spinner>
                                    </div>
                                ) : updateHistory.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        Nenhuma atualização disponível
                                    </div>
                                ) : (
                                    <Table bordered className="no-hover-table" style={{ margin: 0 }}>
                                        <thead style={{ backgroundColor: '#e9ecef' }}>
                                            <tr>
                                                <th style={{
                                                    textAlign: 'center',
                                                    fontWeight: 'bold',
                                                    padding: '12px',
                                                    borderRight: '1px solid #dee2e6'
                                                }}>
                                                    Data
                                                </th>
                                                <th style={{
                                                    textAlign: 'center',
                                                    fontWeight: 'bold',
                                                    padding: '12px',
                                                    borderRight: '1px solid #dee2e6'
                                                }}>
                                                    Identificador
                                                </th>
                                                {isAdmin && (
                                                    <th style={{
                                                        textAlign: 'center',
                                                        fontWeight: 'bold',
                                                        padding: '12px'
                                                    }}>
                                                        Ações
                                                    </th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {updateHistory.map((update, index) => (
                                                <tr key={index}>
                                                    <td style={{
                                                        textAlign: 'center',
                                                        padding: '12px',
                                                        borderRight: '1px solid #dee2e6'
                                                    }}>
                                                        {formatDate(update.data)}
                                                    </td>
                                                    <td style={{
                                                        textAlign: 'center',
                                                        padding: '12px',
                                                        borderRight: '1px solid #dee2e6'
                                                    }}>
                                                        {update.identificador}
                                                    </td>
                                                    {isAdmin && (
                                                        <td style={{
                                                            textAlign: 'center',
                                                            padding: '12px'
                                                        }}>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleRestoreBackup(update.caminho, update.identificador)}
                                                                disabled={loading || !isAdmin}
                                                                style={{
                                                                    backgroundColor: '#003B6B',
                                                                    borderColor: '#003B6B',
                                                                    padding: '8px 25px',
                                                                    fontWeight: 'bold',
                                                                    borderRadius: '8px',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                Restaurar
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Configuration Modal */}
                <ConfigModal
                    show={showConfigModal}
                    onHide={() => setShowConfigModal(false)}
                    onSave={handleConfigSave}
                    currentConfig={currentConfig}
                />

                {/* Auto Update Modal */}
                <AutoUpdateModal
                    show={showAutoUpdateModal}
                    onHide={() => setShowAutoUpdateModal(false)}
                    onConfirm={handleAutoUpdate}
                    loading={loading}
                />

                {/* Manual Update Modal */}
                <ManualUpdateModal
                    show={showManualUpdateModal}
                    onHide={() => setShowManualUpdateModal(false)}
                    onConfirm={handleManualUpdate}
                    loading={loading}
                />

                {/* Restore Backup Modal */}
                <RestoreBackupModal
                    show={showRestoreBackupModal}
                    onHide={() => setShowRestoreBackupModal(false)}
                    onConfirm={confirmRestoreBackup}
                    backupName={backupToRestore.nome}
                    loading={loading}
                />
            </Container>
        </div>
    );
};

export default StoragePage;
